<template>
  <div>
    <div class="rounded-2xl p-[1px] bg-[#213E52]/20 shadow-xl shadow-black/30 backdrop-blur-md">
      <div class="rounded-2xl bg-gradient-to-b from-[#101832]/70 to-[#080C19]/80 p-3 sm:p-4 md:p-6 flex flex-col min-w-0 glass-panel"
           style="font-family: Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial">

        <div class="bg-gradient-to-r from-[#B8DAFF] to-[#1A6ECA] bg-clip-text text-transparent text-xl md:text-2xl font-semibold">
          Stake your VRX
        </div>
        <p class="text-white/50 text-sm mt-1">
          Stake your VRX to start earning USDT Rewards
        </p>

        <div class="my-3 h-px bg-[#213E52]"></div>

        <div class="w-full min-w-0">
          <div class="flex items-center justify-between mb-3">
            <p class="text-slate-300 text-sm font-medium">Lock Period</p>
            <p v-if="hasActiveStake && currentLockIndex !== null" class="text-xs text-slate-400">
              Current: <span class="text-sky-400 font-semibold">{{ currentLockPeriodDays }}d</span>
            </p>
          </div>
          
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 w-full">
            <button 
              type="button" 
              @click="stakeLockIndex = 0"
              :disabled="isLockDisabled(0)"
              :class="['lock-pill', stakeLockIndex === 0 ? 'lock-pill-active' : '', isLockDisabled(0) ? 'lock-pill-disabled' : '']"
              :title="isLockDisabled(0) ? 'Cannot stake for less time than current lock' : ''"
            >
              <span class="text-base font-bold">30</span>
              <span class="text-xs opacity-80 ml-1">days</span>
            </button>

            <button 
              type="button" 
              @click="stakeLockIndex = 2"
              :disabled="isLockDisabled(2)"
              :class="['lock-pill', stakeLockIndex === 2 ? 'lock-pill-active' : '', isLockDisabled(2) ? 'lock-pill-disabled' : '']"
              :title="isLockDisabled(2) ? 'Cannot stake for less time than current lock' : ''"
            >
              <span class="text-base font-bold">90</span>
              <span class="text-xs opacity-80 ml-1">days</span>
            </button>

            <button 
              type="button" 
              @click="stakeLockIndex = 3"
              :disabled="isLockDisabled(3)"
              :class="['lock-pill', stakeLockIndex === 3 ? 'lock-pill-active' : '', isLockDisabled(3) ? 'lock-pill-disabled' : '']"
              :title="isLockDisabled(3) ? 'Cannot stake for less time than current lock' : ''"
            >
              <span class="text-base font-bold">180</span>
              <span class="text-xs opacity-80 ml-1">days</span>
            </button>

            <button 
              type="button" 
              @click="stakeLockIndex = 4"
              :disabled="isLockDisabled(4)"
              :class="['lock-pill', stakeLockIndex === 4 ? 'lock-pill-active' : '', isLockDisabled(4) ? 'lock-pill-disabled' : '']"
              :title="isLockDisabled(4) ? 'Cannot stake for less time than current lock' : ''"
            >
              <span class="text-base font-bold">360</span>
              <span class="text-xs opacity-80 ml-1">days</span>
            </button>
          </div>
        </div>

        <div class="mt-4 space-y-3 w-full min-w-0">
          <!-- VRX Amount Input -->
          <div :class="['stake-input-wrapper', vrxExceedsBalance ? 'input-error' : '']">
            <div class="flex items-center justify-between gap-2 mb-2 min-w-0">
              <div class="flex items-center gap-2 min-w-0 shrink">
                <img :src="iconVRX" alt="VRX" class="h-5 w-5 sm:h-6 sm:w-6 aspect-square object-contain shrink-0" />
                <span class="text-slate-300 text-xs sm:text-sm font-medium whitespace-nowrap">VRX Amount</span>
              </div>
              <div class="text-xs text-slate-400 min-w-0 shrink-0">
                <span class="text-white/40 whitespace-nowrap">Balance:</span>
                <span class="ml-1 font-semibold tabular-nums text-[#B8DAFF] inline-block max-w-[100px] truncate align-bottom">
                  {{ vrxBalanceDisplay }}
                </span>
              </div>
            </div>
            <input
              v-model="stakeVrxInput"
              type="number" min="0" :max="vrxBalanceRaw" step="0.0001"
              class="stake-number-input w-full bg-transparent border-none text-slate-100 placeholder-slate-400 focus:outline-none text-lg sm:text-xl font-semibold"
              placeholder="0.00"
            />
            <div v-if="vrxExceedsBalance" class="text-xs text-rose-400 mt-1">
              Insufficient balance
            </div>
          </div>

          <!-- Keys Input -->
          <div :class="['stake-input-wrapper', keysExceedsBalance ? 'input-error' : '']">
            <div class="flex items-center justify-between gap-2 mb-2 min-w-0">
              <div class="flex items-center gap-2 min-w-0 shrink">
                <img :src="iconVRXKEY" alt="Access Keys" class="h-5 w-5 sm:h-6 sm:w-6 aspect-square object-contain shrink-0" />
                <span class="text-slate-300 text-xs sm:text-sm font-medium whitespace-nowrap">Access Keys</span>
              </div>
              <div class="text-xs text-slate-400 min-w-0 shrink-0">
                <span class="text-white/40 whitespace-nowrap">Balance:</span>
                <span class="ml-1 font-semibold tabular-nums text-[#B8DAFF] inline-block max-w-[100px] truncate align-bottom">
                  {{ keysBalanceDisplay }}
                </span>
              </div>
            </div>
            <input
              v-model="stakeKeyInput"
              type="number" min="0" :max="keysBalanceRaw" step="1"
              class="stake-number-input w-full bg-transparent border-none text-slate-100 placeholder-slate-400 focus:outline-none text-lg sm:text-xl font-semibold"
              placeholder="0"
            />
            <div v-if="keysExceedsBalance" class="text-xs text-rose-400 mt-1">
              Insufficient balance
            </div>
          </div>
        </div>

        <!-- Share Calculation Preview -->
        <div class="mt-4 wallet-pill bg-gradient-to-br from-sky-500/10 to-blue-500/5 border-sky-400/20 overflow-hidden flex-col items-stretch w-full min-w-0">
          <div class="text-slate-300 text-xs sm:text-sm font-semibold mb-2">Your Shares Calculation</div>
          
          <div class="space-y-1.5 text-xs min-w-0">
            <div class="flex items-center justify-between gap-2 min-w-0">
              <span class="text-slate-400 truncate min-w-0">Base Shares (VRX × 1)</span>
              <span class="text-slate-200 font-semibold tabular-nums shrink-0">{{ baseShares.toLocaleString('en-US', { maximumFractionDigits: 2 }) }}</span>
            </div>
            
            <div class="flex items-center justify-between gap-2 min-w-0">
              <span class="text-slate-400 truncate min-w-0">Lock Multiplier ({{ lockPeriodDays }} days)</span>
              <span class="text-sky-400 font-semibold shrink-0">× {{ lockMultiplier }}</span>
            </div>
            
            <div v-if="keysCount > 0" class="flex items-center justify-between gap-2 min-w-0">
              <span class="text-slate-400 truncate min-w-0">Keys Multiplier ({{ keysCount }} {{ keysCount === 1 ? 'key' : 'keys' }})</span>
              <span class="text-sky-400 font-semibold shrink-0">× {{ keysMultiplier }}</span>
            </div>
            
            <div class="h-px bg-slate-700/50 my-2"></div>
            
            <div class="flex items-center justify-between gap-2 min-w-0">
              <span class="text-[#B8DAFF] font-semibold shrink-0">New Shares Added</span>
              <span class="text-[#B8DAFF] font-bold text-sm tabular-nums shrink-0">{{ totalShares.toLocaleString('en-US', { maximumFractionDigits: 2 }) }}</span>
            </div>
          </div>
        </div>

        <!-- Total Shares After Staking (only shown when topping up existing stake) -->
        <div v-if="hasActiveStake && hasValidInput" class="mt-3 wallet-pill bg-gradient-to-br from-emerald-500/10 to-green-500/5 border-emerald-400/30 overflow-hidden flex-col items-stretch w-full min-w-0">

          
          <div class="space-y-1.5 text-xs min-w-0">
            <div class="flex items-center justify-between gap-2 min-w-0">
              <span class="text-slate-400 truncate min-w-0">Total VRX</span>
              <span class="text-slate-200 font-semibold tabular-nums shrink-0">{{ totalVRXAfterStaking.toLocaleString('en-US', { maximumFractionDigits: 4 }) }}</span>
            </div>
            
            <div class="flex items-center justify-between gap-2 min-w-0">
              <span class="text-slate-400 truncate min-w-0">Combined Key Multiplier</span>
              <span class="text-emerald-400 font-semibold shrink-0">× {{ totalKeyMultiplierAfterStaking }}</span>
            </div>
            
            <div class="h-px bg-emerald-700/30 my-2"></div>
            
            <div class="flex items-center justify-between gap-2 min-w-0">
              <span class="text-emerald-300 font-bold shrink-0">Total New Shares</span>
              <span class="text-emerald-300 font-bold text-base tabular-nums shrink-0">{{ totalSharesAfterStaking.toLocaleString('en-US', { maximumFractionDigits: 2 }) }}</span>
            </div>
          </div>
        </div>

        <div class="mt-4 pt-3 sm:pt-4 w-full min-w-0">
          <button
            @click="ensureAllowancesAndStake"
            :disabled="!canStake"
            :class="['btn-primary w-full min-w-0', !canStake ? 'btn-disabled' : '']">
            Approve & Stake
          </button>
        </div>

        <div v-if="hasActiveStake" class="ring-grad rounded-2xl mt-3 w-full min-w-0">
          <div class="rounded-[calc(1rem-1px)] bg-[#0078F8]/20 p-3 sm:p-4 text-center w-full min-w-0">
            <p class="text-xs text-[#00AEF8]">
              NOTE: You already have an active stake. You can stake more, however this will refresh your lock period on your stake.
              You cannot stake for less time than your initial stake either.
            </p>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, watch } from 'vue'
import { ethers } from 'ethers'
import iconVRX from '../assets/VRX.png'
import iconVRXKEY from '../assets/VRXKEY.png'

const stakeLockIndex = defineModel('stakeLockIndex')
const stakeVrxInput  = defineModel('stakeVrxInput')
const stakeKeyInput  = defineModel('stakeKeyInput')

const props = defineProps({
  hasActiveStake: Boolean,
  keysMultiplier: [String, Function],
  ensureAllowancesAndStake: Function,
  vrxBal: [String, Number, Object],
  keyBal: [String, Number, Object],
  fmt18: Function,
  fmt0: Function,
  currentLockIndex: { type: Number, default: null },
  stakedVRX: [String, Number, Object],
  stakedKeys: [String, Number, Object],
})

const vrxBalanceRaw = computed(() => {
  if (!props.vrxBal) return 0
  try {
    return Number(ethers.formatUnits(props.vrxBal, 18))
  } catch {
    return 0
  }
})

const keysBalanceRaw = computed(() => {
  if (!props.keyBal) return 0
  try {
    return Number(props.keyBal.toString())
  } catch {
    return 0
  }
})

const vrxBalanceDisplay = computed(() => {
  if (!props.fmt18 || !props.vrxBal) return '0'
  return props.fmt18(props.vrxBal)
})

const keysBalanceDisplay = computed(() => {
  if (!props.fmt0 || !props.keyBal) return '0'
  return props.fmt0(props.keyBal)
})

const vrxExceedsBalance = computed(() => {
  const input = parseFloat(stakeVrxInput.value) || 0
  return input > vrxBalanceRaw.value
})

const keysExceedsBalance = computed(() => {
  const input = parseFloat(stakeKeyInput.value) || 0
  return input > keysBalanceRaw.value
})

const canStake = computed(() => {
  const vrxVal = parseFloat(stakeVrxInput.value) || 0
  const keyVal = parseFloat(stakeKeyInput.value) || 0
  const hasInput = vrxVal > 0 || keyVal > 0
  return hasInput && !vrxExceedsBalance.value && !keysExceedsBalance.value
})

const hasValidInput = computed(() => {
  const vrxVal = parseFloat(stakeVrxInput.value) || 0
  const keyVal = parseFloat(stakeKeyInput.value) || 0
  return (vrxVal > 0 || keyVal > 0) && !vrxExceedsBalance.value && !keysExceedsBalance.value
})

const lockPeriodDays = computed(() => {
  const lockDays = [30, 60, 90, 180, 360]
  return lockDays[stakeLockIndex.value] || 30
})

const lockMultiplier = computed(() => {
  // Updated: 30d=1x, 90d=3x, 180d=6x, 360d=12x
  const multipliers = [1, 1, 3, 6, 12] // indices: 0=30d, 1=60d, 2=90d, 3=180d, 4=360d
  return multipliers[stakeLockIndex.value] || 1
})

const keysCount = computed(() => {
  return parseInt(stakeKeyInput.value) || 0
})

const keysMultiplier = computed(() => {
  const keys = keysCount.value
  if (keys === 0) return 1
  // 2x per key: 1 key = 2x, 2 keys = 4x, 3 keys = 6x, etc.
  return keys * 2
})

const baseShares = computed(() => {
  return parseFloat(stakeVrxInput.value) || 0
})

const totalShares = computed(() => {
  return baseShares.value * lockMultiplier.value * keysMultiplier.value
})

const currentLockPeriodDays = computed(() => {
  if (props.currentLockIndex === null) return null
  const lockDays = [30, 60, 90, 180, 360]
  return lockDays[props.currentLockIndex] || null
})

const isLockDisabled = (lockIndex) => {
  // If no active stake, all options available
  if (!props.hasActiveStake || props.currentLockIndex === null) return false
  // Cannot select a lock period shorter than current lock
  return lockIndex < props.currentLockIndex
}

// Auto-select the current lock period when topping up existing stake
watch(
  () => [props.hasActiveStake, props.currentLockIndex],
  ([hasStake, currentIndex]) => {
    if (hasStake && currentIndex !== null) {
      // If current selection is less than the minimum allowed, set to minimum
      if (stakeLockIndex.value < currentIndex) {
        stakeLockIndex.value = currentIndex
      }
    }
  },
  { immediate: true }
)

// Calculate existing staked amounts
const existingStakedVRX = computed(() => {
  if (!props.stakedVRX) return 0
  try {
    return Number(ethers.formatUnits(props.stakedVRX, 18))
  } catch {
    return 0
  }
})

const existingStakedKeys = computed(() => {
  if (!props.stakedKeys) return 0
  try {
    return Number(props.stakedKeys.toString())
  } catch {
    return 0
  }
})

// Calculate total after adding new stake
const totalVRXAfterStaking = computed(() => {
  return existingStakedVRX.value + baseShares.value
})

const totalKeysAfterStaking = computed(() => {
  return existingStakedKeys.value + keysCount.value
})

const totalKeyMultiplierAfterStaking = computed(() => {
  const totalKeys = totalKeysAfterStaking.value
  if (totalKeys === 0) return 1
  return totalKeys * 2
})

const totalSharesAfterStaking = computed(() => {
  // Total shares = (existing VRX + new VRX) * lock multiplier * (existing keys + new keys) multiplier
  return totalVRXAfterStaking.value * lockMultiplier.value * totalKeyMultiplierAfterStaking.value
})
</script>

<style scoped>
/* Glass Panel Effect */
.glass-panel {
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

/* Hide number input spinners */
.stake-number-input::-webkit-outer-spin-button,
.stake-number-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  appearance: none;
  margin: 0;
}

.stake-number-input[type=number] {
  -moz-appearance: textfield;
  appearance: textfield;
}

.stake-number-input {
  width: 100%;
  min-width: 0;
}

/* Stake Input Wrapper */
.stake-input-wrapper {
  @apply p-3 sm:p-4 rounded-xl transition-all duration-200;
  background: linear-gradient(135deg, rgba(56, 189, 248, 0.08), rgba(59, 130, 246, 0.05));
  border: 1.5px solid rgba(56, 189, 248, 0.2);
  box-shadow: 
    inset 0 1px 1px 0 rgba(255, 255, 255, 0.08),
    0 2px 12px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  max-width: 100%;
}

.stake-input-wrapper:focus-within {
  border-color: rgba(56, 189, 248, 0.4);
  background: linear-gradient(135deg, rgba(56, 189, 248, 0.12), rgba(59, 130, 246, 0.08));
  box-shadow: 
    inset 0 1px 1px 0 rgba(255, 255, 255, 0.1),
    0 4px 20px rgba(56, 189, 248, 0.2);
}

.input-error {
  border-color: rgba(239, 68, 68, 0.5);
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(220, 38, 38, 0.05));
}

.input-error:focus-within {
  border-color: rgba(239, 68, 68, 0.6);
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(220, 38, 38, 0.08));
  box-shadow: 
    inset 0 1px 1px 0 rgba(255, 255, 255, 0.1),
    0 4px 20px rgba(239, 68, 68, 0.3);
}

.btn-disabled {
  @apply opacity-50 cursor-not-allowed;
  pointer-events: none;
  filter: grayscale(0.5);
}

/* Lock Period Pills */
.lock-pill {
  @apply px-3 py-2 rounded-lg transition-all duration-200;
  @apply flex flex-row items-center justify-center;
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.6), rgba(30, 41, 59, 0.5));
  border: 1.5px solid rgba(148, 163, 184, 0.2);
  color: rgb(203, 213, 225);
}

.lock-pill:hover {
  border-color: rgba(56, 189, 248, 0.3);
  background: linear-gradient(135deg, rgba(56, 189, 248, 0.1), rgba(56, 189, 248, 0.05));
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(56, 189, 248, 0.15);
}

.lock-pill:active {
  transform: translateY(0);
}

.lock-pill-active {
  background: linear-gradient(135deg, rgba(56, 189, 248, 0.25), rgba(59, 130, 246, 0.2));
  border-color: rgba(56, 189, 248, 0.5);
  color: rgb(255, 255, 255);
  box-shadow: 
    inset 0 1px 1px 0 rgba(255, 255, 255, 0.15),
    0 4px 16px rgba(56, 189, 248, 0.3);
}

.lock-pill-active:hover {
  transform: translateY(-1px);
  border-color: rgba(56, 189, 248, 0.6);
  box-shadow: 
    inset 0 1px 1px 0 rgba(255, 255, 255, 0.2),
    0 6px 20px rgba(56, 189, 248, 0.4);
}

.lock-pill-disabled {
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.4), rgba(30, 41, 59, 0.3));
  border-color: rgba(148, 163, 184, 0.1);
  color: rgb(148, 163, 184, 0.4);
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.lock-pill-disabled:hover {
  transform: none;
  box-shadow: none;
}
</style>
